import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { APP_PIN_FIRESTORE_FIELD } from '@/constants/appPinLock';
import {
  hashAppPin,
  parseAppPinHashRecord,
  verifyAppPin,
  type AppPinHashRecord,
} from '@/services/appPinCrypto';

export type AppPinLockConfig = {
  enabled: boolean;
  unlock: AppPinHashRecord;
  alt: AppPinHashRecord;
};

export type AppPinMatch = 'unlock' | 'alt' | 'none';

export function parseAppPinLockConfig(raw: unknown): AppPinLockConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.en !== true) return null;
  const unlock = parseAppPinHashRecord(o.u);
  const alt = parseAppPinHashRecord(o.a);
  if (!unlock || !alt) return null;
  return { enabled: true, unlock, alt };
}

export function subscribeAppPinLockConfig(
  uid: string,
  onUpdate: (config: AppPinLockConfig | null) => void,
  onError?: (e: Error) => void
): () => void {
  if (!uid) {
    onUpdate(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      const data = snap.data() as Record<string, unknown> | undefined;
      onUpdate(parseAppPinLockConfig(data?.[APP_PIN_FIRESTORE_FIELD]));
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  );
}

export async function saveAppPinLock(uid: string, unlockPin: string, altPin: string): Promise<void> {
  const [unlock, alt] = await Promise.all([hashAppPin(unlockPin), hashAppPin(altPin)]);
  await setDoc(
    doc(db, 'users', uid),
    {
      [APP_PIN_FIRESTORE_FIELD]: {
        en: true,
        u: unlock,
        a: alt,
      },
    },
    { merge: true }
  );
}

export async function clearAppPinLock(uid: string): Promise<void> {
  await setDoc(
    doc(db, 'users', uid),
    {
      [APP_PIN_FIRESTORE_FIELD]: { en: false },
    },
    { merge: true }
  );
}

/** Always hashes both slots so timing does not reveal which PIN matched. */
export async function matchAppPin(pin: string, config: AppPinLockConfig): Promise<AppPinMatch> {
  const [unlockOk, altOk] = await Promise.all([
    verifyAppPin(pin, config.unlock),
    verifyAppPin(pin, config.alt),
  ]);
  if (unlockOk) return 'unlock';
  if (altOk) return 'alt';
  return 'none';
}
